// reducers/users.js
import { createSlice } from "@reduxjs/toolkit"
import {
  changePassword,
  forgotPassword,
  loginUser,
  resendLoginOtp,
  userDetails,
  verifyLoginOtp,
  verifyOtp,
} from "../actions/authAction"

const initialState = {
  isLoggedIn: false,
  loading: false,
  error: null,
  actionType: null,
  authUser: {},
  message: null,
  forgetError: false,
  forgetSuccessMsg: false,
  verifyPasswordError: null,
  verifyPasswordStep: "VERIFY_OTP",
  varifyPasswordSuccesMsg: null,
  changePasswordError: null,
  changePasswordSuccessMsg: null,
  userRole: [],
  userId: null,
  accountType: null,
  resetPasswordLoading: true,
}

const usersSlice = createSlice({
  name: "login",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.loading = true
        state.error = null
        state.isLoggedIn = false
        state.actionType = null
        state.message = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        if (!action.payload) {
          state.error = "Something went wrong. Please try again."
          return
        }
        const { status } = action.payload
        if (status) {
          const { data, msg } = action.payload
          if (data?.requiresOtp) {
            state.actionType = "LOGIN_OTP_REQUIRED"
            state.message = msg
            sessionStorage.setItem("loginEmail", data.email)
            sessionStorage.setItem("loginOtpSentAt", Date.now())
            action.meta.arg.history("/login/verify-otp")
            return
          }
          state.actionType = "LOGIN_SUCCESS"
          state.authUser = data
          state.isLoggedIn = true
          state.message = msg
          localStorage.setItem("authUser", JSON.stringify(data))
          localStorage.setItem("isLoggedIn", true)
          localStorage.setItem("accountType", data?.accountType)
          state.userRole = data?.role
          state.accountType = data?.accountType
          sessionStorage.removeItem("loginEmail")
          action.meta.arg.history("/dashboard")
        } else {
          state.actionType = action.payload.errorCode || "WRONG_PASSWORD"
          state.error = action.payload.msg
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload?.msg ||
          action.error?.message ||
          "Login failed. Please try again."
        state.authUser = {}
        state.isLoggedIn = false
      })

    builder
      .addCase(verifyLoginOtp.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false
        const { status } = action.payload
        if (status) {
          const { data, msg } = action.payload
          state.actionType = "LOGIN_SUCCESS"
          state.authUser = data
          state.isLoggedIn = true
          state.message = msg
          localStorage.setItem("authUser", JSON.stringify(data))
          localStorage.setItem("isLoggedIn", true)
          localStorage.setItem("accountType", data?.accountType)
          state.userRole = data?.role
          state.accountType = data?.accountType
          sessionStorage.removeItem("loginEmail")
          action.meta.arg.history("/dashboard")
        } else {
          state.actionType = "LOGIN_OTP_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.msg
      })

    builder
      .addCase(resendLoginOtp.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(resendLoginOtp.fulfilled, state => {
        state.loading = false
      })
      .addCase(resendLoginOtp.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.msg
      })

    // USER RELATED API
    builder
      .addCase(userDetails.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(userDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          localStorage.setItem("authUser", JSON.stringify(data))
          state.userRole = data?.role
          state.userId = data._id
        }
      })
      .addCase(userDetails.rejected, (state, action) => {
        state.loading = false
      })

    builder
      .addCase(forgotPassword.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        const forgotEmail = action.meta.arg.email
        const { status } = action.payload
        if (status) {
          state.actionType = "SENT_MAIL"
          state.message = action.payload.msg
          state.verifyPasswordStep = "VERIFY_OTP"
          sessionStorage.setItem("forgotEmail", forgotEmail)
          sessionStorage.setItem("forgotOtpSentAt", Date.now())
          sessionStorage.removeItem("forgotOtp")
          state.loading = false
          state.forgetSuccessMsg = action.payload.msg
          action.meta.arg.history("/verify-password")
        } else {
          state.actionType = "SENT_MAIL_FAILED"
          state.error = action.payload.msg
          state.loading = false
        }
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false
        state.forgetError = action.payload.msg
      })
    builder
      .addCase(verifyOtp.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "VERIFIED_OTP"
          state.verifyPasswordStep = "NEW_PASSWORD"
          state.message = action.payload.msg
          if (action.meta.arg?.otp) {
            sessionStorage.setItem("forgotOtp", action.meta.arg.otp)
          }
        } else {
          state.actionType = "VERIFIED_OTP_FAILED"
          state.error = action.payload.msg
          state.loading = false
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false
        state.verifyPasswordError = action.payload.msg
      })
    builder
      .addCase(changePassword.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
        state.message = null
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "NEW_PASSWORD_SUCCESS"
          state.verifyPasswordStep = "VERIFY_OTP"
          sessionStorage.removeItem("forgotEmail")
          sessionStorage.removeItem("forgotOtp")
          action.meta.arg.history("/login")
          state.message = action.payload.msg
        } else {
          state.actionType = "NEW_PASSWORD_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false
        state.changePasswordError = action.payload.msg
      })
  },
})

export default usersSlice.reducer
